import {useMemo, useState} from "react";
import {Button, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {TimeMark} from "../../../../sdo/TimeMark.ts";
import TimeMarkCard from "../../intel/panels/timeline/TimeMarkCard.tsx";

let nextId = 100;

function createEmptyTimeMark(): TimeMark {
    return {
        uid: String(nextId++),
        time: Date.now(),
        precision: "DAY",
        title: "",
        details: "",
    };
}

// TODO: Replace with API call to TimelineManager.getMarks(profileType, typeKey, defKey, matchHash)
function buildTestData(): TimeMark[] {
    return [
        {
            uid: "1",
            time: new Date(2026, 3, 1, 0, 0, 0).getTime(),
            precision: "DAY",
            title: "Initial publication",
            details: "Quarterly Intelligence Report Q1 2026 published by the Strategic Analysis Group. Distributed through partner network channels.",
        },
        {
            uid: "2",
            time: new Date(2026, 3, 5, 11, 0, 0).getTime(),
            precision: "DAY_HM",
            title: "Mirrored to partner archive",
            details: "Document copied to the partner research archive for long-term preservation.",
        },
        {
            uid: "3",
            time: new Date(2026, 3, 10, 0, 0, 0).getTime(),
            precision: "DAY",
            title: "Editorial revision applied",
            details: "Minor corrections applied to appendix C following analyst review. Original publication date preserved.",
        },
        {
            uid: "4",
            time: new Date(2025, 0, 1, 0, 0, 0).getTime(),
            precision: "YR",
            title: "Source dataset coverage start",
            details: "",
        },
    ];
}

function UXResourceTimelinePanel() {
    const [marks, setMarks] = useState<TimeMark[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [newMarkUid, setNewMarkUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return marks;
        const q = filterText.toLowerCase();
        return marks.filter((m) => JSON.stringify(m).toLowerCase().includes(q));
    }, [marks, filterText]);

    const handleDelete = (uid: string) => {
        // TODO: Replace with API call to TimelineManager.deleteMark()
        setMarks((prev) => prev.filter((m) => m.uid !== uid));
        if (newMarkUid === uid) setNewMarkUid(null);
    };

    const handleUpdate = (updated: TimeMark) => {
        // TODO: Replace with API call to TimelineManager.saveMark()
        setMarks((prev) => prev.map((m) => m.uid === updated.uid ? updated : m));
    };

    const handleNew = () => {
        const newMark = createEmptyTimeMark();
        setMarks((prev) => [newMark, ...prev]);
        setNewMarkUid(newMark.uid);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Profile Time Marks</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter time marks..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>

                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {marks.length}
                </Typography>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={handleNew}
                    sx={{...appStyles.panelActionButton, position: "absolute", right: 8}}
                >
                    New Mark
                </Button>
            </Stack>

            {/* Time mark cards list */}
            <Stack direction="column" spacing={1} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {filtered.map((mark) => (
                    <TimeMarkCard
                        key={mark.uid}
                        mark={mark}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        startInEditMode={mark.uid === newMarkUid}
                    />
                ))}
                {filtered.length === 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.80rem",
                        textAlign: "center",
                        padding: "24px",
                    }}>
                        No time marks match the current filter.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default UXResourceTimelinePanel;
